import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useStoreSelector } from "../../../../src/lib/store";
import {
  cancelLearn,
  clearControlBinding,
  controlSessionStore,
  editControlBinding,
  startLearn,
} from "../controls/controlSession";
import { describeControlBinding } from "../controls/controlBindings";

export type LearnableControlProps = {
  /** Adam param address, e.g. `/track/0/mix/volume`. */
  path: string;
  children: ReactNode;
  className?: string;
  /**
   * Amber corner marker when bound. Sliders set this false and tint the thumb
   * instead (`data-bound` on `.adam-slider`).
   */
  boundMark?: boolean;
};

/**
 * Right-click Learn / Edit / Clear (Shady-style). Bound state defaults to an
 * amber corner marker; pass `boundMark={false}` when the child paints its own.
 */
export function LearnableControl({
  path,
  children,
  className = "",
  boundMark = true,
}: LearnableControlProps) {
  const learningTarget = useStoreSelector(controlSessionStore, s => s.learningTarget);
  const binding = useStoreSelector(controlSessionStore, s =>
    s.bindings.find(b => b.target === path) ?? null,
  );
  const learning = learningTarget === path;
  const bound = binding != null;

  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);

  return (
    <div
      className={["relative", className].filter(Boolean).join(" ")}
      onContextMenu={event => {
        event.preventDefault();
        event.stopPropagation();
        setMenu({ x: event.clientX, y: event.clientY });
      }}
    >
      {children}
      {bound && boundMark ? (
        <span
          className="pointer-events-none absolute top-0 left-0 z-[2] h-1.5 w-1.5 rounded-full border border-amber-200/80 bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.7)]"
          aria-hidden
          title={describeControlBinding(binding.binding)}
        />
      ) : null}
      {learning ? (
        <span
          className="pointer-events-none absolute inset-0 z-[1] rounded-sm ring-1 ring-amber-300/90 shadow-[0_0_0_1px_rgba(252,211,77,0.25),0_0_12px_rgba(251,191,36,0.2)]"
          aria-hidden
        />
      ) : null}
      {menu ? (
        <LearnableContextMenu
          path={path}
          bindingLabel={bound ? describeControlBinding(binding.binding) : null}
          learning={learning}
          point={menu}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </div>
  );
}

function LearnableContextMenu({
  path,
  bindingLabel,
  learning,
  point,
  onClose,
}: {
  path: string;
  bindingLabel: string | null;
  learning: boolean;
  point: { x: number; y: number };
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) {
      return;
    }
    const margin = 8;
    const width = menu.offsetWidth || 160;
    const height = menu.offsetHeight || 80;
    const left = Math.min(
      Math.max(margin, point.x),
      Math.max(margin, window.innerWidth - width - margin),
    );
    const top = Math.min(
      Math.max(margin, point.y),
      Math.max(margin, window.innerHeight - height - margin),
    );
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  }, [point.x, point.y]);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return createPortal(
    <>
      <div
        className="pointer-events-auto fixed inset-0 z-[200] m-0 cursor-default border-none bg-transparent p-0"
        onPointerDown={event => {
          event.preventDefault();
          onClose();
        }}
        onContextMenu={event => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="pointer-events-auto fixed z-[201] min-w-[10rem] max-w-[calc(100vw-16px)] rounded-md border border-white/10 bg-lab-panel p-0.5 text-xs text-slate-200 shadow-[0_12px_28px_rgba(0,0,0,0.35)]"
        role="menu"
        style={{ left: point.x, top: point.y }}
      >
        <MenuItem
          onClick={() => {
            onClose();
            if (learning) {
              cancelLearn();
            } else {
              startLearn(path);
            }
          }}
        >
          {learning ? "Cancel learn" : "Learn"}
        </MenuItem>
        {bindingLabel ? (
          <>
            <div className="px-2 py-1 text-[9px] uppercase tracking-[0.14em] text-lab-muted">
              Binding
            </div>
            <div
              className="max-w-[14rem] truncate px-2 py-1 text-[10px] text-slate-400"
              title={bindingLabel}
            >
              {bindingLabel}
            </div>
            <MenuItem
              onClick={() => {
                onClose();
                editControlBinding(path);
              }}
            >
              Edit binding…
            </MenuItem>
            <MenuItem
              danger
              onClick={() => {
                onClose();
                clearControlBinding(path);
              }}
            >
              Clear binding
            </MenuItem>
          </>
        ) : null}
      </div>
    </>,
    document.body,
  );
}

function MenuItem({
  children,
  onClick,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className={[
        "block h-8 w-full rounded border-none bg-transparent px-[0.55rem] text-left text-inherit [font:inherit]",
        danger
          ? "text-rose-200 hover:bg-rose-500/15"
          : "hover:bg-white/8",
      ].join(" ")}
      role="menuitem"
      onMouseDown={event => event.preventDefault()}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}
